/**
 * Notification Settings Component
 * Version: 6.2.0
 * 
 * User preferences for notifications
 */
import { NotificationSettings as NotificationSettingsType, NotificationCategory, NotificationPriority } from '../types/notifications'
import './NotificationSettings.css'

// ============================================
// Toggle Switch Component
// ============================================
interface ToggleSwitchProps {
  checked: boolean
  onChange: (checked: boolean) => void
  label?: string
  disabled?: boolean
}

function ToggleSwitch({ checked, onChange, label, disabled }: ToggleSwitchProps) {
  return (
    <label className={`toggle-switch ${disabled ? 'disabled' : ''}`}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
      />
      <span className="toggle-slider"></span>
      {label && <span className="toggle-label">{label}</span>}
    </label>
  )
}

// ============================================
// Select Dropdown Component
// ============================================
interface SelectDropdownProps {
  value: string
  onChange: (value: string) => void
  options: { value: string; label: string }[]
  label?: string
}

function SelectDropdown({ value, onChange, options, label }: SelectDropdownProps) {
  return (
    <div className="select-dropdown">
      {label && <label className="select-label">{label}</label>}
      <select value={value} onChange={(e) => onChange(e.target.value)}>
        {options.map(opt => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    </div>
  )
}

// ============================================
// Category Toggle Component
// ============================================
interface CategoryToggleProps {
  category: NotificationCategory
  enabled: boolean
  onChange: (enabled: boolean) => void
}

function CategoryToggle({ category, enabled, onChange }: CategoryToggleProps) {
  const categoryInfo: Record<NotificationCategory, { icon: string; label: string; description: string }> = {
    [NotificationCategory.TRADE]: { icon: '📈', label: 'Trade', description: 'Order execution, P&L alerts' },
    [NotificationCategory.ALERT]: { icon: '⚠️', label: 'Alerts', description: 'Price, volume, indicator alerts' },
    [NotificationCategory.SYSTEM]: { icon: '🔧', label: 'System', description: 'Maintenance, updates' },
    [NotificationCategory.BOT]: { icon: '🤖', label: 'Bots', description: 'Bot start/stop/error' },
    [NotificationCategory.PORTFOLIO]: { icon: '💼', label: 'Portfolio', description: 'P&L, margin, drawdown' },
    [NotificationCategory.MARKET]: { icon: '📊', label: 'Market', description: 'Market open/close, holidays' },
  }

  const info = categoryInfo[category]

  return (
    <div className={`category-toggle ${enabled ? 'enabled' : ''}`}>
      <div className="category-info">
        <span className="category-icon">{info.icon}</span>
        <div className="category-text">
          <span className="category-label">{info.label}</span>
          <span className="category-description">{info.description}</span>
        </div>
      </div>
      <ToggleSwitch checked={enabled} onChange={onChange} />
    </div>
  )
}

// ============================================
// Main Settings Panel Component
// ============================================
interface NotificationSettingsProps {
  settings: NotificationSettingsType
  onUpdate: (settings: Partial<NotificationSettingsType>) => void
  onReset: () => void
  isOpen: boolean
  onClose: () => void
}

export function NotificationSettings({ settings, onUpdate, onReset, isOpen, onClose }: NotificationSettingsProps) {
  if (!isOpen) return null

  const priorityOptions = [
    { value: 'low', label: 'Low & Above' },
    { value: 'normal', label: 'Normal & Above' },
    { value: 'high', label: 'High & Above' },
    { value: 'urgent', label: 'Urgent Only' },
  ]

  return (
    <div className="settings-overlay" onClick={onClose}>
      <div className="settings-panel" onClick={(e) => e.stopPropagation()}>
        <div className="settings-header">
          <h2>Notification Settings</h2>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="settings-content">
          {/* General Settings */}
          <section className="settings-section">
            <h3 className="section-title">General</h3>
            
            <div className="setting-row">
              <div className="setting-info">
                <span className="setting-label">Enable Notifications</span>
                <span className="setting-description">Receive notifications from ZO Bull</span>
              </div>
              <ToggleSwitch 
                checked={settings.enabled} 
                onChange={(checked) => onUpdate({ enabled: checked })} 
              />
            </div>

            <div className="setting-row">
              <div className="setting-info">
                <span className="setting-label">Sound</span>
                <span className="setting-description">Play sound for new notifications</span>
              </div>
              <ToggleSwitch 
                checked={settings.sound} 
                onChange={(checked) => onUpdate({ sound: checked })}
                disabled={!settings.enabled}
              />
            </div>

            <div className="setting-row">
              <div className="setting-info">
                <span className="setting-label">Push Notifications</span>
                <span className="setting-description">Browser push notifications</span>
              </div>
              <ToggleSwitch 
                checked={settings.pushEnabled} 
                onChange={(checked) => onUpdate({ pushEnabled: checked })}
                disabled={!settings.enabled}
              />
            </div>

            <div className="setting-row">
              <div className="setting-info">
                <span className="setting-label">Email Notifications</span>
                <span className="setting-description">Daily summary via email</span>
              </div>
              <ToggleSwitch 
                checked={settings.emailEnabled} 
                onChange={(checked) => onUpdate({ emailEnabled: checked })}
                disabled={!settings.enabled}
              />
            </div>
          </section>

          {/* Category Settings */}
          <section className="settings-section">
            <h3 className="section-title">Categories</h3>
            <p className="section-description">Choose which notification types to receive</p>
            
            <div className="category-toggles">
              <CategoryToggle
                category={NotificationCategory.TRADE}
                enabled={settings.tradeEnabled}
                onChange={(checked) => onUpdate({ tradeEnabled: checked })}
              />
              <CategoryToggle
                category={NotificationCategory.ALERT}
                enabled={settings.alertEnabled}
                onChange={(checked) => onUpdate({ alertEnabled: checked })}
              />
              <CategoryToggle
                category={NotificationCategory.SYSTEM}
                enabled={settings.systemEnabled}
                onChange={(checked) => onUpdate({ systemEnabled: checked })}
              />
              <CategoryToggle
                category={NotificationCategory.BOT}
                enabled={settings.botEnabled}
                onChange={(checked) => onUpdate({ botEnabled: checked })}
              />
              <CategoryToggle
                category={NotificationCategory.PORTFOLIO}
                enabled={settings.portfolioEnabled}
                onChange={(checked) => onUpdate({ portfolioEnabled: checked })}
              />
              <CategoryToggle
                category={NotificationCategory.MARKET}
                enabled={settings.marketEnabled}
                onChange={(checked) => onUpdate({ marketEnabled: checked })}
              />
            </div>
          </section>

          {/* Priority Settings */}
          <section className="settings-section">
            <h3 className="section-title">Priority Filter</h3>
            <p className="section-description">Only receive notifications at or above this priority</p>
            
            <SelectDropdown
              value={settings.minPriorityAlert}
              onChange={(value) => onUpdate({ minPriorityAlert: value as NotificationPriority })}
              options={priorityOptions}
            />
          </section>

          {/* Quiet Hours */}
          <section className="settings-section">
            <h3 className="section-title">Quiet Hours</h3>
            <p className="section-description">Mute notifications during specified hours</p>
            
            <div className="setting-row">
              <div className="setting-info">
                <span className="setting-label">Enable Quiet Hours</span>
                <span className="setting-description">Mute notifications at night</span>
              </div>
              <ToggleSwitch 
                checked={settings.quietHoursEnabled} 
                onChange={(checked) => onUpdate({ quietHoursEnabled: checked })}
                disabled={!settings.enabled}
              />
            </div>

            {settings.quietHoursEnabled && (
              <div className="quiet-hours-inputs">
                <div className="time-input">
                  <label>From</label>
                  <input
                    type="time"
                    value={settings.quietHoursStart || '22:00'}
                    onChange={(e) => onUpdate({ quietHoursStart: e.target.value })}
                    disabled={!settings.enabled}
                  />
                </div>
                <div className="time-input">
                  <label>To</label>
                  <input
                    type="time"
                    value={settings.quietHoursEnd || '07:00'}
                    onChange={(e) => onUpdate({ quietHoursEnd: e.target.value })}
                    disabled={!settings.enabled}
                  />
                </div>
              </div>
            )}
          </section>
        </div>

        <div className="settings-footer">
          <button className="reset-btn" onClick={onReset}>
            Reset to Defaults
          </button>
          <button className="save-btn" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>
  )
}